// Constantes métier de CarburantTrack : rôles (page 15 du dossier), organisations, installations,
// confidentialité, états de validation (page 41), unités et produits initiaux (D-011).

export const ROLES = [
  'administrateur_national',
  'ministre_cabinet',
  'secretaire_general',
  'directeur_general',
  'directeur',
  'chef_service',
  'superviseur',
  'agent_administratif',
  'agent_terrain',
  'inspecteur',
  'depot',
  'distributeur',
  'transporteur',
  'station',
  'etablissement_prioritaire',
  'caistab',
  'externe_habilite',
  'support_technique',
  'administrateur_idg',
  'autorite_provinciale',
  'citoyen',
] as const;
export type Role = (typeof ROLES)[number];

/**
 * Portée organisationnelle d'un rôle :
 * - `toutes` : l'affectation voit les objets de toutes les organisations dans son périmètre territorial ;
 * - `propre` : l'affectation ne voit que les objets de son organisation et ceux partagés par une relation ;
 * - `aucune` : aucun accès aux données métier (support technique, IDG) ;
 * - `publique` : uniquement les ressources de niveau public.
 */
export type PorteeOrganisation = 'toutes' | 'propre' | 'aucune' | 'publique';

export interface DefinitionRole {
  code: Role;
  libelle: string;
  description: string;
  mfaObligatoire: boolean;
  porteeOrganisation: PorteeOrganisation;
  /** Une affectation de ce rôle doit porter une date de fin. */
  dateFinObligatoire: boolean;
}

export const DEFINITIONS_ROLES: Record<Role, DefinitionRole> = {
  administrateur_national: {
    code: 'administrateur_national',
    libelle: 'Administrateur national',
    description: 'Administration fonctionnelle (DGH) : comptes, référentiels, seuils, validation des installations',
    mfaObligatoire: true,
    porteeOrganisation: 'toutes',
    dateFinObligatoire: false,
  },
  ministre_cabinet: {
    code: 'ministre_cabinet',
    libelle: 'Ministre et cabinet',
    description: 'Consultation des agrégats nationaux et provinciaux, décisions, rapports',
    mfaObligatoire: true,
    porteeOrganisation: 'toutes',
    dateFinObligatoire: false,
  },
  secretaire_general: {
    code: 'secretaire_general',
    libelle: 'Secrétaire général',
    description: 'Consultation des agrégats, suivi des décisions et des rapports',
    mfaObligatoire: true,
    porteeOrganisation: 'toutes',
    dateFinObligatoire: false,
  },
  directeur_general: {
    code: 'directeur_general',
    libelle: 'Directeur général',
    description: 'Supervision opérationnelle et réglementaire dans le périmètre fonctionnel',
    mfaObligatoire: true,
    porteeOrganisation: 'toutes',
    dateFinObligatoire: false,
  },
  directeur: {
    code: 'directeur',
    libelle: 'Directeur',
    description: 'Périmètre de sa direction',
    mfaObligatoire: false,
    porteeOrganisation: 'toutes',
    dateFinObligatoire: false,
  },
  chef_service: {
    code: 'chef_service',
    libelle: 'Chef de service',
    description: 'Périmètre de son service',
    mfaObligatoire: false,
    porteeOrganisation: 'toutes',
    dateFinObligatoire: false,
  },
  superviseur: {
    code: 'superviseur',
    libelle: 'Superviseur de zone',
    description: 'Supervision d’une zone territoriale',
    mfaObligatoire: false,
    porteeOrganisation: 'toutes',
    dateFinObligatoire: false,
  },
  agent_administratif: {
    code: 'agent_administratif',
    libelle: 'Agent administratif',
    description: 'Saisie et tenue des référentiels',
    mfaObligatoire: false,
    porteeOrganisation: 'toutes',
    dateFinObligatoire: false,
  },
  agent_terrain: {
    code: 'agent_terrain',
    libelle: 'Agent de terrain',
    description: 'Relevés et constats de terrain dans son périmètre',
    mfaObligatoire: false,
    porteeOrganisation: 'toutes',
    dateFinObligatoire: false,
  },
  inspecteur: {
    code: 'inspecteur',
    libelle: 'Inspecteur',
    description: 'Inspections et contrôles dans son périmètre autorisé',
    mfaObligatoire: false,
    porteeOrganisation: 'toutes',
    dateFinObligatoire: false,
  },
  depot: {
    code: 'depot',
    libelle: 'Responsable de dépôt',
    description: 'Gestion physique de son dépôt',
    mfaObligatoire: false,
    porteeOrganisation: 'propre',
    dateFinObligatoire: false,
  },
  distributeur: {
    code: 'distributeur',
    libelle: 'Distributeur',
    description: 'Gestion de son réseau, cloisonné vis-à-vis des autres distributeurs',
    mfaObligatoire: false,
    porteeOrganisation: 'propre',
    dateFinObligatoire: false,
  },
  transporteur: {
    code: 'transporteur',
    libelle: 'Transporteur',
    description: 'Flotte, conducteurs et missions confiées',
    mfaObligatoire: false,
    porteeOrganisation: 'propre',
    dateFinObligatoire: false,
  },
  station: {
    code: 'station',
    libelle: 'Gérant de station',
    description: 'Déclarations et suivi de sa station',
    mfaObligatoire: false,
    porteeOrganisation: 'propre',
    dateFinObligatoire: false,
  },
  etablissement_prioritaire: {
    code: 'etablissement_prioritaire',
    libelle: 'Établissement prioritaire',
    description: 'Besoins et suivi de son établissement',
    mfaObligatoire: false,
    porteeOrganisation: 'propre',
    dateFinObligatoire: false,
  },
  caistab: {
    code: 'caistab',
    libelle: 'CAISTAB',
    description: 'Suivi agrégé des volumes pour la stabilisation et la péréquation',
    mfaObligatoire: false,
    porteeOrganisation: 'toutes',
    dateFinObligatoire: false,
  },
  externe_habilite: {
    code: 'externe_habilite',
    libelle: 'Externe habilité',
    description: 'Accès externe borné par une date de fin obligatoire',
    mfaObligatoire: false,
    porteeOrganisation: 'propre',
    dateFinObligatoire: true,
  },
  support_technique: {
    code: 'support_technique',
    libelle: 'Support technique',
    description: 'Journaux techniques et état des services, sans accès métier',
    mfaObligatoire: false,
    porteeOrganisation: 'aucune',
    dateFinObligatoire: false,
  },
  administrateur_idg: {
    code: 'administrateur_idg',
    libelle: 'Administrateur Infinity Digital Gabon',
    description: 'Exploitation technique sans accès métier',
    mfaObligatoire: true,
    porteeOrganisation: 'aucune',
    dateFinObligatoire: false,
  },
  autorite_provinciale: {
    code: 'autorite_provinciale',
    libelle: 'Autorité provinciale',
    description: 'Agrégats, statuts et alertes de sa province',
    mfaObligatoire: false,
    porteeOrganisation: 'toutes',
    dateFinObligatoire: false,
  },
  citoyen: {
    code: 'citoyen',
    libelle: 'Citoyen',
    description: 'Ressources publiques uniquement',
    mfaObligatoire: false,
    porteeOrganisation: 'publique',
    dateFinObligatoire: false,
  },
};

export const ROLES_MFA_OBLIGATOIRE: readonly Role[] = ROLES.filter((r) => DEFINITIONS_ROLES[r].mfaObligatoire);

export const TYPES_ORGANISATIONS = [
  'ministere',
  'dgh',
  'sgepp',
  'sogara',
  'caistab',
  'distributeur',
  'transporteur',
  'station',
  'etablissement_prioritaire',
  'laboratoire',
  'externe',
  'exploitant_technique',
] as const;
export type TypeOrganisation = (typeof TYPES_ORGANISATIONS)[number];

export const LIBELLES_TYPES_ORGANISATIONS: Record<TypeOrganisation, string> = {
  ministere: 'Ministère',
  dgh: 'Direction générale des hydrocarbures',
  sgepp: 'Entreposage (SGEPP)',
  sogara: 'Raffinage (SOGARA)',
  caistab: 'Stabilisation (CAISTAB)',
  distributeur: 'Distributeur',
  transporteur: 'Transporteur',
  station: 'Station-service',
  etablissement_prioritaire: 'Établissement prioritaire',
  laboratoire: 'Laboratoire',
  externe: 'Organisation externe',
  exploitant_technique: 'Exploitant technique',
};

export const TYPES_RELATIONS_ORGANISATIONS = [
  'contrat_transport',
  'reseau_distribution',
  'depot_partage',
  'approvisionnement',
  'tutelle',
  'prestation',
] as const;
export type TypeRelationOrganisation = (typeof TYPES_RELATIONS_ORGANISATIONS)[number];

export const LIBELLES_RELATIONS: Record<TypeRelationOrganisation, string> = {
  contrat_transport: 'Contrat de transport',
  reseau_distribution: 'Réseau de distribution',
  depot_partage: 'Dépôt partagé',
  approvisionnement: 'Approvisionnement',
  tutelle: 'Tutelle',
  prestation: 'Prestation',
};

export const CATEGORIES_INSTALLATIONS = [
  'raffinerie',
  'depot',
  'centre_chargement',
  'centre_emplissage',
  'station',
  'client_industriel',
  'site_strategique',
] as const;
export type CategorieInstallation = (typeof CATEGORIES_INSTALLATIONS)[number];

export const LIBELLES_CATEGORIES: Record<CategorieInstallation, string> = {
  raffinerie: 'Raffinerie',
  depot: 'Dépôt',
  centre_chargement: 'Centre de chargement',
  centre_emplissage: 'Centre d’emplissage GPL',
  station: 'Station-service',
  client_industriel: 'Client industriel',
  site_strategique: 'Site stratégique',
};

export const NIVEAUX_CONFIDENTIALITE = [
  'publique',
  'restreinte',
  'confidentielle',
  'strategique',
  'personnelle',
] as const;
export type NiveauConfidentialite = (typeof NIVEAUX_CONFIDENTIALITE)[number];

export const LIBELLES_CONFIDENTIALITE: Record<NiveauConfidentialite, string> = {
  publique: 'Publique',
  restreinte: 'Diffusion restreinte',
  confidentielle: 'Confidentielle',
  strategique: 'Stratégique',
  personnelle: 'Personnelle',
};

/** Rang croissant de sensibilité ; une permission de niveau N couvre les niveaux de rang inférieur ou égal. */
export function rangConfidentialite(niveau: NiveauConfidentialite): number {
  return NIVEAUX_CONFIDENTIALITE.indexOf(niveau);
}

/** Les neuf états de validation de la page 41 du dossier. */
export const ETATS_VALIDATION = [
  'brouillon',
  'declare',
  'en_attente_validation',
  'verifie',
  'conteste',
  'corrige',
  'valide',
  'annule',
  'archive',
] as const;
export type EtatValidation = (typeof ETATS_VALIDATION)[number];

export const LIBELLES_ETATS: Record<EtatValidation, string> = {
  brouillon: 'Brouillon',
  declare: 'Déclaré',
  en_attente_validation: 'En attente de validation',
  verifie: 'Vérifié',
  conteste: 'Contesté',
  corrige: 'Corrigé',
  valide: 'Validé',
  annule: 'Annulé',
  archive: 'Archivé',
};

export const UNITES = ['L', 'm3', 'kg', 't', 'bouteille'] as const;
export type Unite = (typeof UNITES)[number];

export const DEFINITIONS_UNITES: Record<Unite, { libelle: string; symbole: string; grandeur: 'volume' | 'masse' | 'compte' }> =
  {
    L: { libelle: 'Litre', symbole: 'L', grandeur: 'volume' },
    m3: { libelle: 'Mètre cube', symbole: 'm³', grandeur: 'volume' },
    kg: { libelle: 'Kilogramme', symbole: 'kg', grandeur: 'masse' },
    t: { libelle: 'Tonne', symbole: 't', grandeur: 'masse' },
    bouteille: { libelle: 'Bouteille', symbole: 'bouteille', grandeur: 'compte' },
  };

export interface ProduitInitial {
  code: string;
  nom: string;
  uniteReference: Unite;
  /** Densité de référence en kg par litre, paramétrable et non réglementaire (D-011). */
  densiteReference: string;
  estGpl: boolean;
}

/** Produits initiaux (D-011). Densités de référence à confirmer par les professionnels. */
export const PRODUITS_INITIAUX: readonly ProduitInitial[] = [
  { code: 'super', nom: 'Essence super', uniteReference: 'L', densiteReference: '0.745', estGpl: false },
  { code: 'gasoil', nom: 'Gasoil', uniteReference: 'L', densiteReference: '0.845', estGpl: false },
  { code: 'petrole_lampant', nom: 'Pétrole lampant', uniteReference: 'L', densiteReference: '0.800', estGpl: false },
  { code: 'jet_a1', nom: 'Jet A1', uniteReference: 'L', densiteReference: '0.800', estGpl: false },
  { code: 'fuel_lourd', nom: 'Fuel lourd', uniteReference: 'L', densiteReference: '0.980', estGpl: false },
  { code: 'gpl_butane', nom: 'GPL butane', uniteReference: 'kg', densiteReference: '0.580', estGpl: true },
];

/** Formats de bouteilles initiaux (D-011). */
export const FORMATS_BOUTEILLES_INITIAUX = [
  { code: 'B6', libelle: 'Bouteille de 6 kg', contenuKg: '6' },
  { code: 'B12_5', libelle: 'Bouteille de 12,5 kg', contenuKg: '12.5' },
] as const;

export const ACTIONS = ['lire', 'creer', 'modifier', 'valider', 'exporter', 'administrer'] as const;
export type Action = (typeof ACTIONS)[number];

/** Ressources soumises au moteur d'habilitation. */
export const RESSOURCES = [
  'organisations',
  'utilisateurs',
  'habilitations',
  'parametres',
  'fonctionnalites',
  'installations',
  'reservoirs',
  'vehicules',
  'conducteurs',
  'localites',
  'territoires',
  'catalogue',
  'documents',
  'audit',
  'recherche',
  'synchronisation',
  // Lot 2 : stocks, approvisionnements, alertes (RESSOURCES_LOT2 du sous-module matiere).
  'stocks',
  'mouvements',
  'positions',
  'corrections',
  'anomalies',
  'approvisionnements',
  'gpl',
  'autonomie',
  'alertes',
  'regles_alerte',
  'notifications',
  'imports',
  'echeances',
  'agregats',
  // Lot 6 : inspections et constats (G2), incidents (G3), prix et observations (G5), connecteurs (H6).
  'inspections',
  'constats',
  'incidents',
  'prix',
  'observations_prix',
  'integrations',
  // Lot 3 : logistique, stations, ventes (RESSOURCES_LOT3 du sous-module logistique).
  'besoins',
  'ordres_transport',
  'suivi_transport',
  'receptions',
  'rapprochements',
  'ventes',
  'stations',
  'journaux_techniques',
  'etat_services',
  'parametres_exploitation',
] as const;
export type Ressource = (typeof RESSOURCES)[number];

/** Ressources techniques : seules accessibles au support technique et à l'administrateur IDG. */
export const RESSOURCES_TECHNIQUES: readonly Ressource[] = [
  'journaux_techniques',
  'etat_services',
  'parametres_exploitation',
];

export const FONCTIONNALITES = ['portail_public', 'assistant_llm', 'suivi_economique', 'fond_carte_externe'] as const;
export type Fonctionnalite = (typeof FONCTIONNALITES)[number];

export const LIBELLES_FONCTIONNALITES: Record<Fonctionnalite, string> = {
  portail_public: 'Portail public',
  assistant_llm: 'Assistant avec modèle de langage',
  suivi_economique: 'Suivi économique',
  fond_carte_externe: 'Fond de carte externe',
};

export const SOURCES_DONNEES = ['saisie', 'import', 'api', 'systeme', 'hors_ligne'] as const;
export type SourceDonnee = (typeof SOURCES_DONNEES)[number];

export const THEMES = ['jour', 'nuit', 'systeme'] as const;
export type Theme = (typeof THEMES)[number];

export const DENSITES_AFFICHAGE = ['confortable', 'compacte'] as const;
/** Tailles de texte des préférences (D-038 : trois tailles). */
export const TAILLES_TEXTE = ['petite', 'normale', 'grande'] as const;
export type TailleTexte = (typeof TAILLES_TEXTE)[number];
export type DensiteAffichage = (typeof DENSITES_AFFICHAGE)[number];

export const ENVIRONNEMENTS = ['presentation', 'recette', 'production'] as const;
export type Environnement = (typeof ENVIRONNEMENTS)[number];

export const CODES_PROVINCES = ['GA-1', 'GA-2', 'GA-3', 'GA-4', 'GA-5', 'GA-6', 'GA-7', 'GA-8', 'GA-9'] as const;
export type CodeProvince = (typeof CODES_PROVINCES)[number];

export const TYPES_OBJETS_DOCUMENTS = ['installation', 'organisation', 'vehicule'] as const;
export type TypeObjetDocument = (typeof TYPES_OBJETS_DOCUMENTS)[number];

/** Types de fichiers acceptés (vérifiés par nombre magique) et taille maximale. */
export const TYPES_FICHIERS_ACCEPTES = ['pdf', 'jpeg', 'png', 'xlsx', 'csv'] as const;
export type TypeFichier = (typeof TYPES_FICHIERS_ACCEPTES)[number];
export const TAILLE_MAX_DOCUMENT_OCTETS = 10 * 1024 * 1024;

/** Seuils de fraîcheur par défaut (D-022), en heures. */
export const SEUILS_FRAICHEUR_DEFAUT = { recenteHeures: 24, ancienneHeures: 72 } as const;

/** Distance en dessous de laquelle deux installations au nom proche sont un doublon potentiel. */
export const DISTANCE_DOUBLON_METRES = 300;
